import DialogComp from 'components/Dialog';
import { Button } from 'components/ui/button';
import { Label } from 'components/ui/label';
import { Select, SelectContent, SelectGroup, SelectItem, SelectTrigger, SelectValue } from 'components/ui/select';
import { Textarea } from 'components/ui/textarea';
import React from 'react';
import { EditingContext } from '../../containers/EditingContext';
import { AlertCircle, PlusIcon } from 'lucide-react';
import { Input } from 'components/ui/input';
import ToolTipComponent from 'components/ToolTip';
import { tryParseJSONObject } from 'utils/index';

// Try to format values so that not every value ends up being a string
const jsonFormatValues = (value: string) => {
  // Number
  if (Number(value) && !Number.isNaN(Number(value))) return Number(value);

  // Object hopefully
  if (typeof value === 'string' && value.includes(':')) return JSON.parse(value);

  // Array hopefully
  if (typeof value === 'string' && value.includes('[') && value.includes(']')) {
    const stringObj = `{ "array": ${value} }`;
    const parsedArray = JSON.parse(stringObj).array;
    return parsedArray;
  }

  // String with extra quotes removed
  return value.replaceAll('"', '');
};

const OrgSettings = ({ thisOrgData }: IOrgSettings) => {
  const { submitOrgSettingsJSON } = React.useContext(EditingContext);
  const orgSettings = thisOrgData.orgSettings;
  const availableApplications = orgSettings ? orgSettings?.map(set => set.application) : [];
  const [selectedApplication, setselectedApplication] = React.useState<string>('');

  // Settings keys, with stringified values
  const [settingKeysPairs, setSettingKeysPairs] = React.useState<Record<string, string> | null>(null);

  React.useEffect(() => {
    if (selectedApplication) {
      setSettingKeysPairs(null);
      setChangedKeys({});
      const appDataSettings = Object.assign(
        {},
        orgSettings?.find(set => set.application === selectedApplication)?.settings,
      );
      if (!appDataSettings) return;
      let keys = [];
      keys = appDataSettings && Object.keys(appDataSettings);
      keys?.forEach(key => (appDataSettings[key] = JSON.stringify(appDataSettings[key])));
      setSettingKeysPairs(appDataSettings);
    }
  }, [selectedApplication]);

  const [newApplicationData, setnewApplicationData] = React.useState<{ name: string; json: string } | null>(null);
  const onChangeNewAppData = (field: string, value: string) => {
    if (newApplicationData) {
      setnewApplicationData({ ...newApplicationData, [field]: value });
    } else {
      setnewApplicationData({ name: '', json: '', [field]: value });
    }
  };

  const [changedKeys, setChangedKeys] = React.useState<Record<string, any>>({});

  const onChangeSettingKeys = (key: string, value: any) => {
    setChangedKeys({ ...changedKeys, [key]: value });
  };

  const appendedSettings = { ...settingKeysPairs, ...changedKeys };

  const [newKeyDataPair, setNewKeyDataPair] = React.useState<{ name: string; data: string } | null>(null);
  const onChangeNewKeyDataPair = (field: string, value: string) => {
    if (newKeyDataPair) {
      setNewKeyDataPair({ ...newKeyDataPair, [field]: value });
    } else {
      setNewKeyDataPair({ name: '', data: '', [field]: value });
    }
  };

  const postNewData = () => {
    const newJSONObject = appendedSettings;
    const keys = Object.keys(newJSONObject);

    // Format values so that they can ultimately be correctly parsed with JSON.parse
    keys.forEach(key => (newJSONObject[key] = jsonFormatValues(newJSONObject[key])));
    const jsonString = JSON.stringify(newJSONObject);
    submitOrgSettingsJSON(thisOrgData.orgId, selectedApplication, jsonString)?.then(res => {
      if (res) {
        setChangedKeys({});

        const appDataSettings = JSON.parse(jsonString);
        const keys = appDataSettings && Object.keys(appDataSettings);
        keys?.forEach((key: string) => (appDataSettings[key] = JSON.stringify(appDataSettings[key])));
        setSettingKeysPairs(appDataSettings);
      }
    });
  };

  return (
    <>
      <div className="mb-5 flex">
        <Select value={selectedApplication} onValueChange={setselectedApplication}>
          <SelectTrigger className="w-[180px]">
            <SelectValue placeholder="Select application" />
          </SelectTrigger>
          <SelectContent>
            <SelectGroup>
              {availableApplications.map(app => (
                <SelectItem key={app} value={app}>
                  {app}
                </SelectItem>
              ))}
            </SelectGroup>
          </SelectContent>
        </Select>
        <div className="flex w-full justify-end">
          <DialogComp
            openButton={
              <Button variant="outline" className="h-9">
                <PlusIcon strokeWidth={2} />
                <div>New application</div>
              </Button>
            }
            title="New application"
            content={
              <div className="grid gap-y-4 w-4xl min-h-15" style={{ whiteSpace: 'pre', overflow: 'scroll' }}>
                <div>
                  <Label htmlFor="name">Application name</Label>
                  <Input
                    type="text"
                    id="name"
                    className="w-100"
                    value={newApplicationData?.name || ''}
                    onChange={e => onChangeNewAppData('name', e.target.value)}
                    placeholder="Application name"
                  />
                </div>
                <div>
                  <Label htmlFor="newJson">JSON clause</Label>
                  <Textarea
                    id="newJson"
                    placeholder="Insert JSON"
                    value={newApplicationData?.json || ''}
                    onChange={e => onChangeNewAppData('json', e.target.value)}
                    style={{ fontSize: '16px', height: '10rem' }}
                  />
                </div>
              </div>
            }
            cancel={{ callback: () => '' }}
            accept={{
              callback: () => {
                if (newApplicationData)
                  submitOrgSettingsJSON(thisOrgData.orgId, newApplicationData?.name, newApplicationData?.json)?.then(
                    res => {
                      if (res) setnewApplicationData(null);
                    },
                  );
              },
              disabled: !newApplicationData?.name || !newApplicationData?.json,
            }}
          />
        </div>
      </div>
      <div className="flex">
        {settingKeysPairs && (
          <div className="flex w-full justify-end">
            <DialogComp
              openButton={
                <Button variant="outline" className="h-9">
                  <PlusIcon strokeWidth={2} />
                  <div>New key</div>
                </Button>
              }
              title="New key"
              content={
                <div className="grid gap-y-4 w-4xl min-h-15" style={{ whiteSpace: 'pre', overflow: 'scroll' }}>
                  <div>
                    <Label htmlFor="keyname">Key name</Label>
                    <Input
                      type="text"
                      id="keyname"
                      className="w-100"
                      value={newKeyDataPair?.name || ''}
                      onChange={e => onChangeNewKeyDataPair('name', e.target.value)}
                      placeholder="Key name"
                    />
                  </div>
                  <div>
                    <Label htmlFor="data">Data</Label>
                    <Textarea
                      id="data"
                      placeholder="Insert data"
                      value={newKeyDataPair?.data || ''}
                      onChange={e => onChangeNewKeyDataPair('data', e.target.value)}
                      style={{ fontSize: '16px', height: '10rem' }}
                    />
                  </div>
                </div>
              }
              cancel={{ callback: () => '' }}
              accept={{
                callback: () => {
                  if (newKeyDataPair) {
                    const newData =
                      Number(newKeyDataPair.data) && !Number.isNaN(Number(newKeyDataPair.data))
                        ? Number(newKeyDataPair.data)
                        : newKeyDataPair.data;

                    setChangedKeys({ ...changedKeys, [newKeyDataPair.name]: newData || '{}' });
                    setNewKeyDataPair(null);
                  }
                },
                disabled: !newKeyDataPair?.name,
              }}
            />
          </div>
        )}
      </div>

      <div className="grid gap-y-4">
        {appendedSettings &&
          Object.keys(appendedSettings).map(key => (
            <div key={key} className="flex">
              <div className="w-[70vw] lg:w-240 xl:w-7xl">
                <Label htmlFor={key} className="select-text">
                  {key}
                </Label>
                <Textarea
                  id={key}
                  placeholder={key}
                  value={
                    appendedSettings[key] && typeof appendedSettings[key] !== 'string'
                      ? JSON.stringify(appendedSettings[key])
                      : appendedSettings[key]
                        ? appendedSettings[key]
                        : ''
                  }
                  onChange={e => onChangeSettingKeys(key, e.target.value)}
                  style={{ fontSize: '16px', height: '10rem' }}
                />
              </div>

              <div className="w-25 mt-7 justify-center grid">
                {Object.keys(changedKeys).find(ck => ck === key) && (
                  <div>
                    <Button className="h-14" onClick={() => postNewData()}>
                      <div>Post</div>
                    </Button>
                    {!tryParseJSONObject(appendedSettings[key]) ? (
                      <ToolTipComponent
                        trigger={
                          <div className="flex w-full justify-center my-5">
                            <AlertCircle />
                          </div>
                        }
                        content={<div>{'Might not be valid JSON, as it has failed to parse.'}</div>}
                      />
                    ) : (
                      <></>
                    )}
                  </div>
                )}
                {/* <div className="flex justify-center">
                  <DialogComp
                    openButton={
                      <Button size={'icon'}>
                        <TrashIcon />
                      </Button>
                    }
                    title="Do you really want to delete this key?"
                    cancel={{ callback: () => '' }}
                    accept={{
                      callback: () => {
                        if (selectedApplication) {
                          deleteGlobalSettings(selectedApplication, key).then(res => {
                            if (res) {
                              getSettings();
                            }
                          });
                        }
                      },
                    }}
                  />
                </div> */}
              </div>
            </div>
          ))}
      </div>
    </>
  );
};

interface IOrgSettings {
  thisOrgData: IOrg;
}

export default OrgSettings;
